import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { AuthService, CurrentUserResponse } from '@core/api/auth/auth.service';
import { PasswordChangeCheckService } from '@core/services/password-change-check.service';
import { of } from 'rxjs';
import { map, catchError, tap } from 'rxjs/operators';

export const authGuard: CanActivateFn = (route, state) => {
    const auth = inject(AuthService);
    const router = inject(Router);
    const passwordCheckService = inject(PasswordChangeCheckService);

    // Helper function to handle user routing
    const handleUserRouting = (userData: any, currentUrl: string): boolean => {
        if (userData?.is_new) {
            // If user is new and trying to access new-user-password route, allow it
            if (currentUrl === '/new-user-password' || currentUrl.startsWith('/new-user-password')) {
                return true;
            }
            // Otherwise redirect to new password page
            router.navigate(['/new-user-password'], { replaceUrl: true });
            return false;
        } else {
            // If user is not new and trying to access new-user-password route, redirect to home
            if (currentUrl === '/new-user-password' || currentUrl.startsWith('/new-user-password')) {
                router.navigate(['/home'], { replaceUrl: true });
                return false;
            }

            // Check password expiry after successful authentication (but not on change-password page)
            if (currentUrl !== '/change-password' && !currentUrl.startsWith('/change-password')) {
                setTimeout(() => passwordCheckService.checkPasswordExpiry(), 100);
            }

            // Otherwise allow access to requested route
            return true;
        }
    };

    // Helper function to handle authentication failure
    const handleAuthFailure = () => {
        auth.clearUser();
        router.navigate(['/login'], { replaceUrl: true });
        return of(false);
    };

    // If user data is already cached
    if (auth.user()) {
        return of(handleUserRouting(auth.user(), state.url));
    }

    // Fetch user data if not cached
    return auth.getCurrentUser().pipe(
        map((response: CurrentUserResponse) => {
            if (response.data) {
                auth.setUser(response.data);
                return handleUserRouting(response.data, state.url);
            }
            router.navigate(['/login'], { replaceUrl: true });
            return false;
        }),
        catchError((error) => {
            console.error('Failed to fetch user data:', error);
            return handleAuthFailure();
        })
    );
};
